import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Menu, MenuItem, Badge, ListItemText } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useMe } from '../context/MeContext';
import { getPendingMembers } from '../api/saasApi';

/**
 * Collapses the clinic-admin destinations into a single "Admin" dropdown, and
 * the platform super-admin destinations into a separate "Platform" one, so the
 * top bar reads the same for every user apart from those two entries.
 */

const useMenu = () => {
  const [anchor, setAnchor] = useState(null);
  return {
    anchor,
    open: (e) => setAnchor(e.currentTarget),
    close: () => setAnchor(null),
    isOpen: Boolean(anchor),
  };
};

export const AdminMenu = () => {
  const navigate = useNavigate();
  const { me, can } = useMe();
  const menu = useMenu();
  const [pendingCount, setPendingCount] = useState(0);

  // Every entry here acts on a clinic. Platform admins pass every permission
  // check but often belong to no clinic, so require an actual tenant.
  const hasClinic = !!me?.tenant;
  const canApprove = hasClinic && can('user:approve');

  const items = !hasClinic ? [] : [
    can('user:view') && { to: '/users', label: 'Clinicians', badge: canApprove ? pendingCount : 0 },
    can('role:view') && { to: '/roles', label: 'Roles & permissions' },
    can('billing:view') && { to: '/billing', label: 'Billing' },
    can('tenant:settings') && { to: '/clinic', label: 'Clinic settings' },
  ].filter(Boolean);

  // Surface waiting join requests without making the admin go looking for them.
  useEffect(() => {
    if (!canApprove) return undefined;

    let cancelled = false;
    const load = async () => {
      try {
        const res = await getPendingMembers();
        if (!cancelled) setPendingCount((res.data?.data || []).length);
      } catch (e) {
        /* a failed count must never break the nav bar */
      }
    };

    load();
    const timer = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [canApprove]);

  if (items.length === 0) return null;

  const go = (to) => {
    menu.close();
    navigate(to);
  };

  return (
    <>
      <Button color="inherit" onClick={menu.open} endIcon={<ArrowDropDownIcon />}>
        <Badge color="error" badgeContent={canApprove ? pendingCount : 0} sx={{ '& .MuiBadge-badge': { right: -6, top: 2 } }}>
          Admin
        </Badge>
      </Button>
      <Menu anchorEl={menu.anchor} open={menu.isOpen} onClose={menu.close}>
        {items.map((item) => (
          <MenuItem key={item.to} onClick={() => go(item.to)}>
            <ListItemText>{item.label}</ListItemText>
            {item.badge > 0 && (
              <Badge color="error" badgeContent={item.badge} sx={{ ml: 3, '& .MuiBadge-badge': { position: 'static', transform: 'none' } }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export const PlatformMenu = () => {
  const navigate = useNavigate();
  const { isPlatformAdmin } = useMe();
  const menu = useMenu();

  if (!isPlatformAdmin) return null;

  const go = (to) => {
    menu.close();
    navigate(to);
  };

  const items = [
    { to: '/platform', label: 'Overview' },
    { to: '/platform/tenants', label: 'Clinics' },
    { to: '/platform/users', label: 'Users' },
    { to: '/platform/audit', label: 'Audit log' },
  ];

  return (
    <>
      <Button color="inherit" onClick={menu.open} endIcon={<ArrowDropDownIcon />}>
        Platform
      </Button>
      <Menu anchorEl={menu.anchor} open={menu.isOpen} onClose={menu.close}>
        {items.map((item) => (
          <MenuItem key={item.to} onClick={() => go(item.to)}>
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
